import { useMutation, useQueryClient } from '@tanstack/react-query';

import { clientService } from '@/services/client-service';
import { pollUntilCondition } from '@/utils/poll';
import type { UUID } from '@/utils/uuid';

import { clientMutationKeys, clientQueryKeys } from './query-keys';

function sameSiteSet(a: string[], b: string[]): boolean {
  const norm = (list: string[]) => new Set(list.map((s) => s.toLowerCase()));
  const left = norm(a);
  const right = norm(b);
  if (left.size !== right.size) return false;
  for (const site of left) {
    if (!right.has(site)) return false;
  }
  return true;
}

/**
 * Update an enrolled agent's sites (capabilities) via the register endpoint,
 * then wait until the read model reflects the new set before resolving. The
 * connected agent adopts the change live over the stream (no re-enroll/restart).
 */
export function useRegisterClient(tenantId?: UUID) {
  const qc = useQueryClient();

  return useMutation({
    mutationKey: clientMutationKeys.registerClient(tenantId),
    mutationFn: async ({
      clientId,
      capabilities,
    }: {
      clientId: UUID;
      capabilities: string[];
    }) => {
      if (!tenantId) throw new Error('Missing tenant id');

      const success = await clientService.registerClient(
        tenantId,
        clientId,
        capabilities
      );
      if (!success) {
        throw new Error('Failed to update sites');
      }

      await pollUntilCondition(async () => {
        const client = await clientService.findClient(tenantId, clientId);
        return !!client && sameSiteSet(client.capabilities ?? [], capabilities);
      });

      return clientId;
    },
    onSuccess: async (clientId) => {
      await qc.invalidateQueries({
        queryKey: clientQueryKeys.listByTenant(tenantId),
        refetchType: 'all',
      });
      await qc.invalidateQueries({
        queryKey: clientQueryKeys.detail(tenantId, clientId),
      });
    },
  });
}
